export const M5_UNIT_3 = {
  id: "m5-u3-past-tobe",
  title: "Unit 3: Past Simple - To Be",
  topic: "Talking about the past with was/were",
  iconType: "clock",
  grammarImage: "/grammar/grammar-m5-u3.jpg",
  grammarText: "The past simple of 'to be' has two forms: 'was' (I/he/she/it) and 'were' (you/we/they). We use it to describe past states, locations, feelings, and situations. Negative forms are 'wasn't' (was not) and 'weren't' (were not). Questions start with Was/Were + subject. This tense helps you talk about where you were, how you felt, what your job was, and describe past situations. It's one of the most common past tenses in English!",

  sets: [
    // ===================================================================
    // SET 1: WAS/WERE AFFIRMATIVE (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u3-s1",
      title: "Set 1: Introducing Was & Were",
      questions: [
        {
          type: "match",
          q: "Match the subject to the correct form:",
          pairs: [
            { left: "I", right: "was" },
            { left: "You", right: "were" },
            { left: "She", right: "was" }
          ],
          a: "completed",
          tip: "I/he/she/it = WAS. You/we/they = WERE. Important pattern! 📝"
        },
        {
          type: "mc",
          q: "Complete: I ___ at home yesterday.",
          options: ["was", "were", "am"],
          a: "was",
          successtip: "✓ I WAS! Use 'was' with I/he/she/it! 🏠"
        },
        {
          type: "input",
          q: "Complete: They ___ at the party last night.",
          a: "were",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WERE! They/we/you always use 'were'! 🎉"
        },
        {
          type: "mc",
          q: "My phone ___ dead all morning.",
          options: ["was", "were", "is"],
          a: "was",
          successtip: "✓ WAS! Phone = it = was! 📱🔋"
        },
        {
          type: "match",
          q: "Present vs Past:",
          pairs: [
            { left: "I am tired (now)", right: "I was tired (yesterday)" },
            { left: "They are here (now)", right: "They were here (yesterday)" },
            { left: "She is online (now)", right: "She was online (yesterday)" }
          ],
          a: "completed",
          successtip: "✓ Present: am/is/are → Past: was/were! ⏰"
        },
        {
          type: "input",
          q: "He ___ my best friend in high school.",
          a: "was",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WAS! He = was (past). Now maybe: He IS my colleague! 👥"
        },
        {
          type: "order",
          q: "Arrange the sentence:",
          words: ["We", "were", "at", "the", "concert"],
          a: "We were at the concert",
          tip: "We/they = were! 🎵"
        },
        {
          type: "mc",
          q: "The WiFi ___ so slow yesterday!",
          options: ["was", "were", "is"],
          a: "was",
          successtip: "✓ WAS! WiFi = it = was! Now it IS fast again! 📶"
        },
        {
          type: "input",
          q: "You ___ right about the movie. It was boring!",
          a: "were",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WERE! 'You' always uses 'were' in the past! 🎬"
        },
        {
          type: "mc",
          q: "CONTRAST: Last year I ___ a student. Now I ___ a graphic designer.",
          options: ["was / am", "were / am", "was / is"],
          a: "was / am",
          successtip: "✓ I WAS (past) / I AM (now)! Career change! 🎨💼"
        }
      ]
    },

    // ===================================================================
    // SET 2: PAST LOCATIONS (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u3-s2",
      title: "Set 2: Where Were You?",
      questions: [
        {
          type: "match",
          q: "Match the places:",
          pairs: [
            { left: "I was at the gym", right: "Exercise location" },
            { left: "She was in Paris", right: "City/country" },
            { left: "They were at work", right: "Job location" }
          ],
          a: "completed",
          tip: "Use 'at' for specific places, 'in' for cities/countries! 📍"
        },
        {
          type: "mc",
          q: "Where were you last weekend? I ___ at the beach!",
          options: ["was", "were", "am"],
          a: "was",
          successtip: "✓ I WAS at the beach! Now I AM back home! 🏖️"
        },
        {
          type: "input",
          q: "They ___ in Mexico City for vacation.",
          a: "were",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WERE! They = were. Great vacation spot! 🇲🇽"
        },
        {
          type: "mc",
          q: "Last year, she ___ in college. Now she ___ working remotely.",
          options: ["was / is", "were / is", "was / are"],
          a: "was / is",
          successtip: "✓ WAS in college → IS working! Present continuous for current action! 💻"
        },
        {
          type: "order",
          q: "Put the words in order:",
          words: ["He", "was", "at", "the", "hospital", "yesterday"],
          a: "He was at the hospital yesterday",
          tip: "Was + at + place + time! 🏥"
        },
        {
          type: "input",
          q: "We ___ at a restaurant when the lights went out!",
          a: "were",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WERE! Exciting story! 🍽️💡"
        },
        {
          type: "mc",
          q: "The app ___ down for 3 hours yesterday. Now it ___ working fine.",
          options: ["was / is", "were / are", "was / are"],
          a: "was / is",
          successtip: "✓ The app WAS down → IS working! Technical problems! 📱"
        },
        {
          type: "match",
          q: "Then vs Now:",
          pairs: [
            { left: "I was at home (yesterday)", right: "I'm at the office (now)" },
            { left: "They were in class (morning)", right: "They're at lunch (now)" },
            { left: "She was online (earlier)", right: "She's sleeping (now)" }
          ],
          a: "completed",
          successtip: "✓ Contrasting past location with present! 📍⏰"
        },
        {
          type: "input",
          q: "My parents ___ at my graduation ceremony. It was special!",
          a: "were",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WERE! Beautiful moment! 🎓❤️"
        },
        {
          type: "mc",
          q: "I ___ stuck in traffic for 2 hours. Now I ___ finally home!",
          options: ["was / am", "were / am", "was / is"],
          a: "was / am",
          successtip: "✓ I WAS stuck → I AM home! Traffic problems! 🚗😤"
        }
      ]
    },

    // ===================================================================
    // SET 3: FEELINGS & STATES (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u3-s3",
      title: "Set 3: How Were You Feeling?",
      questions: [
        {
          type: "match",
          q: "Match the feelings:",
          pairs: [
            { left: "I was tired", right: "Needed sleep" },
            { left: "She was excited", right: "Very happy" },
            { left: "They were nervous", right: "Worried" }
          ],
          a: "completed",
          tip: "Was/were + adjective = past feeling! 😊😰😴"
        },
        {
          type: "mc",
          q: "I ___ so stressed before the exam. Now I ___ relaxed!",
          options: ["was / am", "were / am", "was / is"],
          a: "was / am",
          successtip: "✓ I WAS stressed (before) → I AM relaxed (now)! 📚✅"
        },
        {
          type: "input",
          q: "The concert ___ amazing! Everyone was dancing!",
          a: "was",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WAS! The concert = it = was! 🎸🎤"
        },
        {
          type: "mc",
          q: "We ___ so bored at the meeting yesterday. Now we ___ working on something fun!",
          options: ["were / are", "was / are", "were / is"],
          a: "were / are",
          successtip: "✓ We WERE bored → We ARE working! Present continuous! 💼➡️🎨"
        },
        {
          type: "order",
          q: "Arrange the sentence:",
          words: ["She", "was", "really", "happy", "about", "the", "news"],
          a: "She was really happy about the news",
          tip: "Was + adjective describes past feelings! 😊"
        },
        {
          type: "input",
          q: "My phone ___ dead, so I couldn't call you!",
          a: "was",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WAS! Phone = it. Now it IS charged! 📱🔋"
        },
        {
          type: "mc",
          q: "The movie ___ boring. Now I ___ watching a better one!",
          options: ["was / am", "were / am", "was / are"],
          a: "was / am",
          successtip: "✓ The movie WAS boring → I AM watching! Present continuous for 'right now'! 🎬"
        },
        {
          type: "match",
          q: "Past feelings vs Present:",
          pairs: [
            { left: "I was angry (then)", right: "I'm calm (now)" },
            { left: "He was confused (before)", right: "He's understanding (now)" },
            { left: "They were sad (yesterday)", right: "They're happy (today)" }
          ],
          a: "completed",
          successtip: "✓ Emotions change over time! 😤→😌"
        },
        {
          type: "input",
          q: "You ___ right! The restaurant was closed on Mondays.",
          a: "were",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WERE! You = were. Good advice! ✅"
        },
        {
          type: "mc",
          q: "Last week I ___ sick in bed. Now I ___ feeling much better!",
          options: ["was / am", "were / am", "was / is"],
          a: "was / am",
          successtip: "✓ I WAS sick → I AM feeling! Recovery! 🤒➡️😊"
        }
      ]
    },

    // ===================================================================
    // SET 4: NEGATIVE FORMS (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u3-s4",
      title: "Set 4: Wasn't & Weren't",
      questions: [
        {
          type: "match",
          q: "Match the contractions:",
          pairs: [
            { left: "was not", right: "wasn't" },
            { left: "were not", right: "weren't" },
            { left: "is not", right: "isn't (present)" }
          ],
          a: "completed",
          tip: "Contractions are more natural in speaking! 🗣️"
        },
        {
          type: "mc",
          q: "I ___ at the party. I was at home studying.",
          options: ["wasn't", "weren't", "isn't"],
          a: "wasn't",
          successtip: "✓ WASN'T! I = wasn't. Missing out! 🏠📚"
        },
        {
          type: "input",
          q: "They ___ happy with the service at the restaurant.",
          a: "weren't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["were not"] },
          successtip: "✓ WEREN'T! They = weren't. Bad experience! 🍽️👎"
        },
        {
          type: "mc",
          q: "The test ___ easy, but I ___ prepared, so I did well!",
          options: ["wasn't / was", "weren't / were", "wasn't / were"],
          a: "wasn't / was",
          successtip: "✓ The test WASN'T easy BUT I WAS prepared! Success! 📝✅"
        },
        {
          type: "order",
          q: "Put in order:",
          words: ["She", "wasn't", "feeling", "well", "yesterday"],
          a: "She wasn't feeling well yesterday",
          tip: "Wasn't + verb-ing = past continuous negative! 🤒"
        },
        {
          type: "input",
          q: "We ___ online when you sent the message.",
          a: "weren't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["were not"] },
          successtip: "✓ WEREN'T! Sorry, missed it! 📱💬"
        },
        {
          type: "mc",
          q: "CONTRAST: Last year he ___ a content creator. He ___ working in marketing. Now he IS an influencer!",
          options: ["wasn't / was", "weren't / was", "wasn't / were"],
          a: "wasn't / was",
          successtip: "✓ He WASN'T → He WAS → Now he IS! Career path! 📱💼"
        },
        {
          type: "match",
          q: "Match the negative situations:",
          pairs: [
            { left: "It wasn't expensive", right: "Good price" },
            { left: "He wasn't ready", right: "Unprepared" },
            { left: "They weren't late", right: "On time" }
          ],
          a: "completed",
          successtip: "✓ Negatives describe what DIDN'T happen! ❌"
        },
        {
          type: "input",
          q: "My dream ___ to be a doctor, but now I'm a content creator and I make much more money!",
          a: "wasn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["was not"] },
          successtip: "✓ WASN'T! Dreams change! Sometimes for the better! 💰📱"
        },
        {
          type: "mc",
          q: "The instructions ___ clear, so everyone ___ confused. Now they ARE much better!",
          options: ["weren't / was", "wasn't / were", "weren't / were"],
          a: "weren't / was",
          successtip: "✓ WEREN'T clear → everyone WAS confused → ARE better now! 📋"
        }
      ]
    },

    // ===================================================================
    // SET 5: DESCRIBING JOBS & ROLES (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u3-s5",
      title: "Set 5: Past Jobs & Roles",
      questions: [
        {
          type: "match",
          q: "Match people to their past jobs:",
          pairs: [
            { left: "Steve Jobs", right: "Was co-founder of Apple" },
            { left: "Shakira", right: "Was a child performer" },
            { left: "Elon Musk", right: "Was a programmer" }
          ],
          a: "completed",
          tip: "Famous people had different jobs before! 🌟"
        },
        {
          type: "mc",
          q: "Five years ago, I ___ a barista. Now I ___ a software developer!",
          options: ["was / am", "were / am", "was / is"],
          a: "was / am",
          successtip: "✓ I WAS a barista → I AM a developer! Career change! ☕→💻"
        },
        {
          type: "input",
          q: "She ___ a teacher for 10 years before she became a YouTuber.",
          a: "was",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WAS! Teacher → YouTuber! Modern career path! 👩‍🏫→📹"
        },
        {
          type: "mc",
          q: "We ___ freelancers last year. Now we ___ running our own agency!",
          options: ["were / are", "was / are", "were / is"],
          a: "were / are",
          successtip: "✓ We WERE freelancers → We ARE running! Present continuous for current situation! 💼"
        },
        {
          type: "order",
          q: "Arrange the sentence:",
          words: ["He", "was", "an", "engineer", "before", "starting", "his", "company"],
          a: "He was an engineer before starting his company",
          tip: "Past job + before + new career! 🏗️→🚀"
        },
        {
          type: "input",
          q: "They ___ students when they created the app. Now they're millionaires!",
          a: "were",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WERE! Student startup success story! 🎓→💰"
        },
        {
          type: "mc",
          q: "In 2019, he ___ working in a call center. Now he ___ a podcast host!",
          options: ["was / is", "were / is", "was / are"],
          a: "was / is",
          successtip: "✓ He WAS working → He IS (now)! From call center to podcasting! 📞→🎙️"
        },
        {
          type: "match",
          q: "Career changes:",
          pairs: [
            { left: "Was a waiter → Is a chef", right: "Restaurant career" },
            { left: "Was an intern → Is a manager", right: "Corporate growth" },
            { left: "Was unemployed → Is an entrepreneur", right: "Startup journey" }
          ],
          a: "completed",
          successtip: "✓ People's careers evolve! 📈"
        },
        {
          type: "input",
          q: "My dad ___ a taxi driver. Now he's an Uber driver with his own schedule!",
          a: "was",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WAS! From taxi to gig economy! 🚕"
        },
        {
          type: "mc",
          q: "She ___ studying marketing, but she ___ enjoying it. Now she's a graphic designer and she loves it!",
          options: ["was / wasn't", "were / wasn't", "was / weren't"],
          a: "was / wasn't",
          successtip: "✓ She WAS studying BUT WASN'T enjoying → Now she loves design! 🎨❤️"
        }
      ]
    },

    // ===================================================================
    // SET 6: MIXED PRACTICE (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u3-s6",
      title: "Set 6: Real-Life Situations",
      questions: [
        {
          type: "match",
          q: "Match the situations:",
          pairs: [
            { left: "The store was closed", right: "Couldn't shop" },
            { left: "We were late", right: "Missed the bus" },
            { left: "It wasn't working", right: "Technical problem" }
          ],
          a: "completed",
          tip: "Was/were describes past situations! 🔄"
        },
        {
          type: "mc",
          q: "The concert ___ sold out, but we ___ lucky and got tickets at the door!",
          options: ["was / were", "were / was", "was / was"],
          a: "was / were",
          successtip: "✓ The concert WAS → We WERE! Lucky! 🎫🎉"
        },
        {
          type: "input",
          q: "I ___ trying to call you, but your phone was off!",
          a: "was",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WAS trying! Past continuous = was/were + verb-ing! 📞"
        },
        {
          type: "mc",
          q: "Last night, the WiFi ___ working. Now it ___ perfect!",
          options: ["wasn't / is", "weren't / is", "wasn't / are"],
          a: "wasn't / is",
          successtip: "✓ WASN'T working → IS perfect! Internet problems solved! 📶✅"
        },
        {
          type: "order",
          q: "Put in order:",
          words: ["The", "app", "was", "down", "for", "maintenance"],
          a: "The app was down for maintenance",
          tip: "Common tech situation! 🔧"
        },
        {
          type: "input",
          q: "You ___ right about that restaurant. The food was terrible!",
          a: "were",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WERE! You = were. Good warning! 🍽️👎"
        },
        {
          type: "mc",
          q: "STORY: Yesterday ___ crazy! My alarm ___ working, I ___ late to work, and my boss ___ happy!",
          options: ["was / wasn't / was / wasn't", "were / wasn't / were / wasn't", "was / weren't / was / weren't"],
          a: "was / wasn't / was / wasn't",
          successtip: "✓ Bad day story: was/wasn't pattern! 😰"
        },
        {
          type: "match",
          q: "Then vs Now:",
          pairs: [
            { left: "I was learning (last year)", right: "I'm working (now)" },
            { left: "They were struggling (before)", right: "They're succeeding (now)" },
            { left: "She was starting (2020)", right: "She's managing (2026)" }
          ],
          a: "completed",
          successtip: "✓ Progress over time! Past continuous → Present continuous! 📈"
        },
        {
          type: "input",
          q: "We ___ expecting so many people at the event!",
          a: "weren't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["were not", "wasn't"] },
          successtip: "✓ WEREN'T! Surprise turnout! 🎉😲"
        },
        {
          type: "mc",
          q: "The prices ___ cheap, but the quality ___ good, so it was worth it!",
          options: ["weren't / was", "wasn't / was", "weren't / were"],
          a: "weren't / was",
          successtip: "✓ WEREN'T cheap BUT WAS good! Quality over price! 💰✅"
        }
      ]
    },

    // ===================================================================
    // SET 7: YES/NO QUESTIONS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u3-s7",
      title: "Set 7: Asking Questions",
      questions: [
        {
          type: "match",
          q: "Match questions to their structure:",
          pairs: [
            { left: "Was he...?", right: "Question with he/she/it" },
            { left: "Were you...?", right: "Question with you/we/they" },
            { left: "Was it...?", right: "Question about things" }
          ],
          a: "completed",
          tip: "Swap subject and verb to make questions! ❓"
        },
        {
          type: "mc",
          q: "___ you at the meeting yesterday?",
          options: ["Were", "Was", "Are"],
          a: "Were",
          successtip: "✓ WERE you...? Natural response: 'Yes, I was' or 'No, I wasn't'! 🏢"
        },
        {
          type: "input",
          q: "___ she online when you messaged her?",
          a: "Was",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WAS she...? Answer: 'Yes, she was' / 'No, she wasn't'! 💬"
        },
        {
          type: "mc",
          q: "A: '___ the concert good?' B: 'Yes, it was amazing!'",
          options: ["Was", "Were", "Is"],
          a: "Was",
          successtip: "✓ WAS it...? Short answer: 'Yes, it was' / 'No, it wasn't'! 🎵"
        },
        {
          type: "order",
          q: "Make a question:",
          words: ["Were", "they", "at", "the", "party?"],
          a: "Were they at the party?",
          tip: "Were + they/we/you + place? 🎉"
        },
        {
          type: "input",
          q: "A: '___ your phone dead?' B: 'Yeah, I forgot to charge it!'",
          a: "Was",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WAS it...? Natural answer: 'Yeah, it was' / 'No, it wasn't'! 📱"
        },
        {
          type: "mc",
          q: "A: '___ you nervous before the exam?' B: 'Yes, I was really nervous!'",
          options: ["Were", "Was", "Are"],
          a: "Were",
          successtip: "✓ WERE you...? Full answer: 'Yes, I was really nervous!' 📚😰"
        },
        {
          type: "match",
          q: "Questions with natural responses:",
          pairs: [
            { left: "Was it expensive?", right: "Yes, it was / No, it wasn't" },
            { left: "Were you tired?", right: "Yes, I was / No, I wasn't" },
            { left: "Was she there?", right: "Yes, she was / No, she wasn't" }
          ],
          a: "completed",
          successtip: "✓ Learn the natural short answers! Very common! 💬"
        },
        {
          type: "input",
          q: "A: '___ the instructions clear?' B: 'No, they weren't. They were confusing!'",
          a: "Were",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WERE they...? Answer: 'No, they weren't' + explanation! 📋"
        },
        {
          type: "mc",
          q: "A: '___ he working from home yesterday?' B: 'Yes, he was.'",
          options: ["Was", "Were", "Is"],
          a: "Was",
          successtip: "✓ WAS he working...? Past continuous question! Short answer: 'Yes, he was'! 💻🏠"
        }
      ]
    },

    // ===================================================================
    // SET 8: WH- QUESTIONS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u3-s8",
      title: "Set 8: Information Questions",
      questions: [
        {
          type: "match",
          q: "Match the question words:",
          pairs: [
            { left: "Where", right: "Location" },
            { left: "What", right: "Thing/situation" },
            { left: "Who", right: "Person" }
          ],
          a: "completed",
          tip: "Question word + was/were + subject! ❓"
        },
        {
          type: "mc",
          q: "___ were you last night? I called you!",
          options: ["Where", "What", "Who"],
          a: "Where",
          successtip: "✓ WHERE = location! Answer: 'I was at the gym' 📍"
        },
        {
          type: "input",
          q: "___ was your favorite teacher in high school?",
          a: "Who",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WHO = person! Answer: 'Mr. Smith was my favorite' 👨‍🏫"
        },
        {
          type: "mc",
          q: "A: '___ was the movie?' B: 'It was really boring.'",
          options: ["How", "Where", "Who"],
          a: "How",
          successtip: "✓ HOW = quality/opinion! Answer describes the experience! 🎬"
        },
        {
          type: "order",
          q: "Make a question:",
          words: ["What", "was", "your", "first", "job?"],
          a: "What was your first job?",
          tip: "What + was + possessive + noun? 💼"
        },
        {
          type: "input",
          q: "A: '___ was she so upset?' B: 'Because she lost her phone!'",
          a: "Why",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WHY = reason! Answer with 'because...'! 😢"
        },
        {
          type: "mc",
          q: "A: '___ were your parents when you graduated?' B: 'They were in the front row!'",
          options: ["Where", "What", "How"],
          a: "Where",
          successtip: "✓ WHERE = location! They were there to support! 🎓❤️"
        },
        {
          type: "match",
          q: "Match questions to answers:",
          pairs: [
            { left: "How was your weekend?", right: "It was great!" },
            { left: "Where were you?", right: "I was at home" },
            { left: "What was the problem?", right: "The WiFi was down" }
          ],
          a: "completed",
          successtip: "✓ Different question words need different answers! 💬"
        },
        {
          type: "input",
          q: "A: '___ was the restaurant?' B: 'It was downtown, near the station.'",
          a: "Where",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WHERE = location! Answer gives place details! 📍🍽️"
        },
        {
          type: "mc",
          q: "A: '___ was he before he became an influencer?' B: 'He was a student!'",
          options: ["What", "Where", "How"],
          a: "What",
          successtip: "✓ WHAT = asking about job/role! Career history question! 💼"
        }
      ]
    },

    // ===================================================================
    // SET 9: CONTRASTING PAST & PRESENT (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u3-s9",
      title: "Set 9: Then vs Now",
      questions: [
        {
          type: "match",
          q: "Match the time contrasts:",
          pairs: [
            { left: "I was broke (2020)", right: "I'm financially stable (now)" },
            { left: "She was shy (before)", right: "She's confident (now)" },
            { left: "They were starting (then)", right: "They're succeeding (now)" }
          ],
          a: "completed",
          tip: "Life changes over time! 🔄"
        },
        {
          type: "mc",
          q: "Five years ago, smartphones ___ everywhere. Now everyone ___ using them constantly!",
          options: ["weren't / is", "wasn't / is", "weren't / are"],
          a: "weren't / is",
          successtip: "✓ WEREN'T everywhere → Everyone IS using! Tech evolution! 📱"
        },
        {
          type: "input",
          q: "In 2010, I ___ in high school. Now I'm running my own business!",
          a: "was",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WAS! From student to entrepreneur! 🎓→💼"
        },
        {
          type: "mc",
          q: "Remote work ___ common before 2020. Now it ___ the new normal!",
          options: ["wasn't / is", "weren't / is", "wasn't / are"],
          a: "wasn't / is",
          successtip: "✓ WASN'T common → IS the new normal! Pandemic changed work! 🏠💻"
        },
        {
          type: "order",
          q: "Put in order:",
          words: ["Last", "year", "I", "was", "learning.", "Now", "I'm", "teaching!"],
          a: "Last year I was learning. Now I'm teaching!",
          tip: "Student → Teacher progression! 📚→👨‍🏫"
        },
        {
          type: "input",
          q: "Social media ___ as big in 2005. Now it's everywhere!",
          a: "wasn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["was not"] },
          successtip: "✓ WASN'T! Social media explosion! 📱📈"
        },
        {
          type: "mc",
          q: "Before, she ___ posting on Instagram. Now she ___ making $10K a month from it!",
          options: ["was / is", "were / is", "was / are"],
          a: "was / is",
          successtip: "✓ WAS posting (hobby) → IS making money (career)! Influencer success! 💰📸"
        },
        {
          type: "match",
          q: "Technology evolution:",
          pairs: [
            { left: "Blockbuster was huge (1990s)", right: "Streaming is king (now)" },
            { left: "Nokia was dominant (2000s)", right: "iPhone is everywhere (now)" },
            { left: "Taxis were main (before)", right: "Uber is popular (now)" }
          ],
          a: "completed",
          successtip: "✓ How industries changed! 🔄📱"
        },
        {
          type: "input",
          q: "Electric cars ___ popular 15 years ago. Now they're mainstream!",
          a: "weren't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["were not"] },
          successtip: "✓ WEREN'T! Tesla changed everything! 🚗⚡"
        },
        {
          type: "mc",
          q: "STORY: In 2015, I ___ working minimum wage. I ___ enjoying it. Now I'm a freelance designer making triple the income!",
          options: ["was / wasn't", "were / wasn't", "was / weren't"],
          a: "was / wasn't",
          successtip: "✓ WAS working BUT WASN'T enjoying → Success story! 💪💰"
        }
      ]
    },

    // ===================================================================
    // SET 10: STORYTELLING (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u3-s10",
      title: "Set 10: Telling Stories",
      questions: [
        {
          type: "match",
          q: "Match story elements:",
          pairs: [
            { left: "Setting", right: "Where/when it was" },
            { left: "Characters", right: "Who was there" },
            { left: "Problem", right: "What was wrong" }
          ],
          a: "completed",
          tip: "Good stories need these elements! 📖"
        },
        {
          type: "mc",
          q: "STORY START: Last Saturday ___ supposed to be relaxing, but everything ___ wrong!",
          options: ["was / went", "were / went", "was / go"],
          a: "was / went",
          successtip: "✓ Saturday WAS (be) + went (go) wrong! Story setup! 😰"
        },
        {
          type: "input",
          q: "CONTINUE: First, my alarm ___ working, so I woke up late.",
          a: "wasn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["was not"] },
          successtip: "✓ WASN'T! Problem #1 introduced! ⏰❌"
        },
        {
          type: "mc",
          q: "CONTINUE: Then I realized my phone ___ charged, and I ___ my charger at work!",
          options: ["wasn't / was", "weren't / was", "wasn't / were"],
          a: "wasn't / was",
          successtip: "✓ WASN'T charged + WAS (left) at work! Problems pile up! 📱🔋"
        },
        {
          type: "order",
          q: "CONTINUE: Put in order:",
          words: ["The", "weather", "was", "terrible", "and", "I", "was", "stuck", "in", "traffic"],
          a: "The weather was terrible and I was stuck in traffic",
          tip: "Multiple problems in one sentence! 🌧️🚗"
        },
        {
          type: "input",
          q: "CONTINUE: When I finally arrived, the place ___ even open!",
          a: "wasn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["was not"] },
          successtip: "✓ WASN'T! Plot twist! 😱"
        },
        {
          type: "mc",
          q: "CONTINUE: I ___ so frustrated! But then I saw my friend, and we ___ laughing about it.",
          options: ["was / were", "were / was", "was / was"],
          a: "was / were",
          successtip: "✓ I WAS frustrated → We WERE laughing! Turning point! 😤→😂"
        },
        {
          type: "match",
          q: "Story structure:",
          pairs: [
            { left: "Beginning: It was...", right: "Set the scene" },
            { left: "Middle: There was a problem", right: "Conflict" },
            { left: "End: Everything was OK", right: "Resolution" }
          ],
          a: "completed",
          successtip: "✓ Classic story structure with 'was/were'! 📚"
        },
        {
          type: "input",
          q: "ENDING: In the end, we ___ at a better place and had an amazing time!",
          a: "were",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WERE! Happy ending! Sometimes bad starts lead to better outcomes! 😊✨"
        },
        {
          type: "mc",
          q: "REFLECTION: The day ___ perfect, but it ___ memorable, and now we're always telling that story!",
          options: ["wasn't / was", "weren't / was", "wasn't / were"],
          a: "wasn't / was",
          successtip: "✓ 🎉 PERFECTO! WASN'T perfect BUT WAS memorable! You mastered 'was/were'! 🏆"
        }
      ]
    }
  ],

 // ===================================================================
// READING SET: My First Job Interview
// ===================================================================
readingSet: {
  id: "m5-u3-reading",
  title: "Reading: My First Job Interview",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: The Disaster (preguntas 1-3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**A Rough Start**

My first job interview was a disaster! I was so nervous. The company was in downtown LA, and I was late because the traffic was terrible. 

When I finally arrived, my shirt was wrinkled and I was sweating. The interviewer was professional, but I wasn't prepared. My answers weren't good, and I wasn't confident at all.`
    },
    {
      type: "mc",
      q: "Why was the person late?",
      options: ["Traffic was terrible", "They overslept", "They got lost"],
      a: "Traffic was terrible",
      successtip: "✓ Traffic was terrible! LA traffic! 🚗"
    },
    {
      type: "mc",
      q: "How was the person feeling during the interview?",
      options: ["Nervous", "Confident", "Excited"],
      a: "Nervous",
      successtip: "✓ They were nervous! First interview jitters! 😰"
    },
    {
      type: "input",
      q: "Fill in the blank: The person ___ prepared for the interview.",
      a: "wasn't",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["was not"] },
      successtip: "✓ WASN'T prepared! Big mistake! 📋❌"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: The Advice & Motivation (preguntas 4-5)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**A Silver Lining**

However, the experience wasn't totally negative. The interviewer was kind and gave me advice. She said I was smart but needed more practice. I wasn't angry or disappointed—I was motivated!`
    },
    {
      type: "mc",
      q: "How did the person feel after receiving advice?",
      options: ["Motivated", "Angry", "Disappointed"],
      a: "Motivated",
      successtip: "✓ They were MOTIVATED! Turned negative into positive! 💪"
    },
    {
      type: "mc",
      q: "The interviewer was ___ to the person.",
      options: ["Kind", "Rude", "Angry"],
      a: "Kind",
      successtip: "✓ The interviewer was KIND! Helpful feedback! 😊"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Five Years Later (preguntas 6-7)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Present Day Success**

That interview was five years ago. Now I'm a senior manager at a tech company, and I'm always telling new employees about my terrible first interview!`
    },
    {
      type: "input",
      q: "How long ago was that interview? ___ years ago.",
      a: "five",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["5", "Five"] },
      successtip: "✓ FIVE years ago! Time flies! ⏰"
    },
    {
      type: "mc",
      q: "What is the person's job now?",
      options: ["Senior manager at a tech company", "Interviewer", "Still unemployed"],
      a: "Senior manager at a tech company",
      successtip: "✓ Senior manager! Success story! 💼🚀"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Reflection & Grammar (preguntas 8-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Looking Back**

Looking back, that day taught me a lot. Even though it started as a disaster, it was the start of a great career path. It proves that one bad day doesn't define your future.`
    },
    {
      type: "mc",
      q: "THEN vs NOW: Before, they ___ prepared. Now they ___ experienced.",
      options: ["weren't / are", "wasn't / is", "weren't / is"],
      a: "weren't / are",
      successtip: "✓ WEREN'T prepared (then) → ARE experienced (now)! 📈"
    },
    {
      type: "input",
      q: "Complete the sentence: The experience ___ totally negative.",
      a: "wasn't",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["was not"] },
      successtip: "✓ WASN'T totally negative! Silver lining! ☁️✨"
    },
    {
      type: "mc",
      q: "What lesson does this story teach?",
      options: ["Bad experiences can lead to growth", "First interviews are always perfect", "Never try again after failure"],
      a: "Bad experiences can lead to growth",
      successtip: "✓ Perfect lesson! Failure → Learning → Success! 🌱📚"
    }
  ]
},
  // ===================================================================
  // REVIEW SET (10 questions)
  // ===================================================================
  reviewSet: {
    id: "m5-u3-review",
    title: "Unit 3 Review: Was/Were Mastery",
    questions: [
      {
        type: "match",
        q: "Match the forms:",
        pairs: [
          { left: "I/he/she/it", right: "was" },
          { left: "you/we/they", right: "were" },
          { left: "Negative", right: "wasn't / weren't" }
        ],
        a: "completed",
        successtip: "✓ Core pattern review! 📝"
      },
      {
        type: "mc",
        q: "Choose correct: She ___ at the gym when I called.",
        options: ["was", "were", "is"],
        a: "was",
        successtip: "✓ WAS! She = was! 🏋️"
      },
      {
        type: "input",
        q: "Complete: They ___ happy with the result.",
        a: "weren't",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["were not", "wasn't"] },
        successtip: "✓ WEREN'T! Negative form! ❌"
      },
      {
        type: "mc",
        q: "Question: ___ you at the party last night?",
        options: ["Were", "Was", "Are"],
        a: "Were",
        successtip: "✓ WERE you...? Answer: 'Yes, I was' / 'No, I wasn't'! 🎉"
      },
      {
        type: "order",
        q: "Make a question:",
        words: ["Where", "was", "she", "yesterday?"],
        a: "Where was she yesterday?",
        tip: "Wh- + was/were + subject! ❓"
      },
      {
        type: "input",
        q: "CONTRAST: Last year I ___ a beginner. Now I'm advanced!",
        a: "was",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓ WAS (past) → AM (present)! Progress! 📈"
      },
      {
        type: "mc",
        q: "The movie ___ boring, but the popcorn ___ good!",
        options: ["was / was", "were / was", "was / were"],
        a: "was / was",
        successtip: "✓ WAS boring + WAS good! Mixed review! 🎬🍿"
      },
      {
        type: "match",
        q: "Match questions to natural answers:",
        pairs: [
          { left: "Was it expensive?", right: "Yes, it was" },
          { left: "Were they late?", right: "No, they weren't" },
          { left: "How was the concert?", right: "It was amazing!" }
        ],
        a: "completed",
        successtip: "✓ Natural conversation patterns! 💬"
      },
      {
        type: "input",
        q: "Five years ago, remote work ___ common. Now it is!",
        a: "wasn't",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["was not"] },
        successtip: "✓ WASN'T! How things changed! 🏠💻"
      },
      {
        type: "mc",
        q: "FINAL: In 2020, I ___ studying. I ___ enjoying it. Now I ___ working in my dream job!",
        options: ["was / wasn't / am", "were / wasn't / am", "was / weren't / am"],
        a: "was / wasn't / am",
        successtip: "✓ 🎉 PERFECTO! WAS studying, WASN'T enjoying → AM working! You mastered past 'to be'! 🏆🎊"
      }
    ]
  }
};
